import React, { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { defineMessages, useIntl } from 'react-intl';
import { Button } from 'primereact/button';
import { Panel } from 'primereact/panel';
import { SplitButton } from 'primereact/splitbutton';
import { useHistory } from 'react-router-dom';
import {
  removeSuperLink,
  sendSuperLinkEmailInvitations,
  sendSuperLinkEmailReminders,
} from '../../../services/apps/SuperLinkService';
import { DeleteTextButton } from '../../../components/button/ActionButton';
import { growlErrors, growlActionSuccess } from '../../../utils/NotificationUtil';
import { searchJobProfile } from '../../../services/apps/JobProfileService';
import { NotificationContext } from '../../../components/GrowlNotifications';
import SuperLinkUrlComponent from './SuperLinkUrlComponent';
import SuperLinkDetailPanel from './SuperLinkDetailPanel';
import SuperLinkJobReportPanel from './SuperLinkJobReportPanel';
import SuperLinkDeleteFormModal from '../components/SuperLinkDeleteFormModal';
import { AccountContext } from '../../../components/AccountContext';
import SuperLinkNotice from './SuperLinkNotice';

const messages = defineMessages({
  successNoti: {
    id: 'noti.success',
    defaultMessage: 'Success!',
  },
  tooltipMsg: {
    id: 'tooltip.download_open_window_hint',
    defaultMessage:
      // eslint-disable-next-line max-len
      'Reports are opened in a <strong>new window</strong>. Please <strong>enable pop-up</strong> windows for this website.',
  },
  sending: {
    id: 'assessment.remind.sending',
    defaultMessage: 'Sending...',
  },
  sendInvite: {
    id: 'assessment.invite.send.plural',
    defaultMessage: 'Send Invites',
  },
  sendRemind: {
    id: 'assessment.remind.send.plural',
    defaultMessage: 'Send Reminders',
  },
  deleteSuperLink: {
    id: 'del_header.super_link',
    defaultMessage: 'Delete SuperLink',
  },
  edit: {
    id: 'actions.edit',
    defaultMessage: 'Edit',
  },
  updateAssessmentsExpiration: {
    id: 'actions.update-assessments-expiration',
    defaultMessage: 'Update Expiration Time',
  },
  resetStatusUpdateAssessmentsExpiration: {
    id: 'actions.reset-status-expired-superlinks',
    defaultMessage: 'Reset Expired Assessments',
  },
});

const isSendInvitationEnabled = (superLink) => {
  const { totalAssessment = 0, totalFinalStateAssessment = 0 } = superLink;

  const asmtNum = parseInt(totalAssessment, 10);
  if (asmtNum <= 0) {
    return false;
  }

  const finalStateNum = parseInt(totalFinalStateAssessment, 10);

  if (finalStateNum <= 0) {
    return true;
  }

  return asmtNum - finalStateNum > 0;
};

const SuperLinkDetails = ({ superLink, assessmentsFilters, toggleEdit, listReport }) => {
  const currentAccount = useContext(AccountContext);
  const growl = useContext(NotificationContext);
  const history = useHistory();
  const { formatMessage } = useIntl();

  const [listJobProfiles, setListJobProfiles] = useState([]);
  const [locale, setLocale] = useState(superLink.locale);
  const [printerFriendly, setPrinterFriendly] = useState(false);
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [jobProfile, setJobProfile] = useState(superLink.targetJob ? superLink.targetJob.id : null);
  const [isSending, setIsSending] = useState(false);
  const [isJpLoading, setIsJpLoading] = useState(false);
  const [emailActionMenu, setEmailActionMenu] = useState([]);

  useEffect(() => {
    const { targetJob, locale, printFriendlyReport } = superLink;
    const query = targetJob ? targetJob.name : '';

    setPrinterFriendly(printFriendlyReport);

    if (locale) {
      setLocale(locale);
    }
    fetchJobs(query);
    buildSendEmailActionMenu();
    // eslint-disable-next-line
  }, []);

  const onSendInvite = async () => {
    const input = {
      superLinkId: superLink.id,
      accountId: currentAccount,
    };

    setIsSending(true);

    try {
      await sendSuperLinkEmailInvitations(input);
      growlActionSuccess(growl, formatMessage(messages.successNoti));
    } catch (err) {
      growlErrors(growl, err);
    } finally {
      setIsSending(false);
    }
  };

  const onSendRemind = async () => {
    const input = {
      superLinkId: superLink.id,
      accountId: currentAccount,
    };

    setIsSending(true);
    try {
      await sendSuperLinkEmailReminders(input);
      growlActionSuccess(growl, formatMessage(messages.successNoti));
    } catch (err) {
      growlErrors(growl, err);
    } finally {
      setIsSending(false);
    }
  };

  const fetchJobs = async (query) => {
    const { assessmentTemplate } = superLink;

    setIsJpLoading(true);

    try {
      const data = await searchJobProfile({
        aid: currentAccount,
        tempId: assessmentTemplate.id,
        query,
      });

      setListJobProfiles(data);
    } catch (err) {
      growlErrors(growl, err);
    } finally {
      setIsJpLoading(false);
    }
  };

  const buildSendEmailActionMenu = () => {
    const emailActionMenu = [
      {
        label: formatMessage(messages.sendInvite),
        command: onSendInvite,
      },
      {
        label: formatMessage(messages.sendRemind),
        command: onSendRemind,
      },
    ];

    setEmailActionMenu(emailActionMenu);
  };

  const deleteSuperLink = () => {
    setShowDeleteConfirmModal(true);
  };

  const cancelDeletingSuperLink = () => {
    setShowDeleteConfirmModal(false);
  };

  const changeSuperLinkPrinterFriendly = (event) => {
    const { target } = event;
    const value = target.type === 'checkbox' ? target.checked : target.value;
    setPrinterFriendly(value);
  };

  const searchJob = async (query) => {
    const { assessmentTemplate } = superLink;
    try {
      setIsJpLoading(true);
      setJobProfile(null);

      const data = await searchJobProfile({
        aid: currentAccount,
        tempId: assessmentTemplate.id,
        query,
      });

      setListJobProfiles(data);
    } catch (err) {
      growlErrors(growl, err);
    } finally {
      setIsJpLoading(false);
    }
  };

  const processDeletingSuperLink = async (formValues) => {
    const updateObject = {
      accountId: currentAccount,
      superLinkId: superLink.id,
      deleteOnlySuperLink: formValues.deleteMode === 'ONLY_SUPERLINK',
      deleteSuperLinkAndRelatedNonEmployeeRespondentsAndAssessments: formValues.deleteMode === 'WITH_NOT_EMPLOYEE',
      deleteSuperLinkAndAllRelatedRespondentsAndAssessments: formValues.deleteMode === 'ALL_RELATED',
    };

    try {
      await removeSuperLink(updateObject);
      const listURL = `/accounts/${currentAccount}/super-links`;
      history.push(listURL);
    } catch (error) {
      growlErrors(growl, error);
    } finally {
      setShowDeleteConfirmModal(false);
    }
  };

  let targetJobProfile = null;
  if (superLink.targetJob && superLink.targetJob !== null) {
    targetJobProfile = jobProfile;
  }

  const reportParams = {
    account: currentAccount,
    locale,
    printerFriendly,
    superLink: superLink.id,
    jobProfile,
    from: assessmentsFilters.from ? assessmentsFilters.from : null,
    to: assessmentsFilters.to ? assessmentsFilters.to : null,
  };

  return (
    <>
      <SuperLinkNotice />
      <Panel header={superLink.name}>
        <div className="p-grid">
          <div key="header" className="p-sm-12">
            <div className="p-grid">
              <div className="p-sm-12 p-md-6 super-link-url-wrapper">
                <SuperLinkUrlComponent superLink={superLink} />
              </div>
              <div className="p-sm-12 p-md-6 align-right">
                <Button
                  type="button"
                  className="p-button-secondary"
                  icon="fa fa-pencil"
                  onClick={() => toggleEdit('core')}
                  tooltip={formatMessage(messages.edit)}
                  tooltipOptions={{ position: 'bottom' }}
                />
                <Button
                  type="button"
                  className="p-button-secondary"
                  icon="far fa-calendar-plus"
                  onClick={() => toggleEdit('updateExpiration')}
                  tooltip={formatMessage(messages.updateAssessmentsExpiration)}
                  tooltipOptions={{ position: 'bottom' }}
                />
                <Button
                  type="button"
                  className="p-button-secondary"
                  icon="far fa-calendar-times"
                  onClick={() => toggleEdit('resetStatusUpdateExpiration')}
                  tooltip={formatMessage(messages.resetStatusUpdateAssessmentsExpiration)}
                  tooltipOptions={{ position: 'bottom' }}
                />{' '}
                <SplitButton
                  label={formatMessage(messages.sendInvite)}
                  icon="pi pi-envelope"
                  onClick={onSendInvite}
                  model={emailActionMenu}
                  disabled={!isSendInvitationEnabled || isSending}
                />{' '}
                <DeleteTextButton
                  position="right"
                  onClick={deleteSuperLink}
                  text={formatMessage(messages.deleteSuperLink)}
                />
              </div>
            </div>
          </div>

          <div className="p-sm-12 p-md-6" key="panels">
            <SuperLinkDetailPanel superLink={superLink} listReport={listReport} />
          </div>
          <div className="p-sm-12 p-md-6">
            <SuperLinkJobReportPanel
              listJobProfiles={listJobProfiles}
              searchJob={searchJob}
              isJpLoading={isJpLoading}
              targetJobProfile={targetJobProfile}
              locale={locale}
              setLocale={setLocale}
              setJobProfile={setJobProfile}
              changeSurveyPrinterFiendly={changeSuperLinkPrinterFriendly}
              reportParams={reportParams}
            />
            <SuperLinkDeleteFormModal
              modalOpen={showDeleteConfirmModal}
              reject={cancelDeletingSuperLink}
              confirm={processDeletingSuperLink}
            />
          </div>
        </div>
      </Panel>
    </>
  );
};

SuperLinkDetails.propTypes = {
  superLink: PropTypes.objectOf(PropTypes.any).isRequired,
  user: PropTypes.objectOf(PropTypes.any).isRequired,
  assessmentsFilters: PropTypes.objectOf(PropTypes.any).isRequired,
  toggleEdit: PropTypes.func.isRequired,
  listReport: PropTypes.string.isRequired,
};

export default SuperLinkDetails;
